import React, {Fragment} from 'react';

import moment from 'moment-tz';
import {Link} from 'react-router';

import {getUserDetailPageURL} from 'app/urls';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import type {SortOptionType} from 'app/utils/constants';
import type {BasicUserSerializedType} from 'sweet-types';

export interface SortedType {
  readonly approved_at?: string | null;
  readonly approved_by?: BasicUserSerializedType | null;
  readonly last_review_at?: string | null;
  readonly next_review_at?: string | null;
  readonly edited_at?: string;
  readonly created_at?: string;
  readonly author?: BasicUserSerializedType | null;
  readonly original_author?: BasicUserSerializedType | null;
}

export const getUserLink = (user: BasicUserSerializedType) => (
  <Link to={getUserDetailPageURL(user.id, 'activity')}>{user.name}</Link>
);
export const getCreatedText = (obj: SortedType): JSX.Element => {
  const params = {
    created_at: moment(obj.created_at).format('lll'),
    timeago: moment(obj.created_at).fromNow(),
  };
  const content =
    obj.original_author != null
      ? gt('Created %(timeago)s by %(user)s', {
          ...params,
          user: getUserLink(obj.original_author),
        })
      : gt('Created %(timeago)s', params);
  return <span title={gt('Created %(created_at)s', params)}>{content}</span>;
};
export const getApprovedText = (obj: SortedType): JSX.Element => {
  const {approved_by} = obj;
  return (
    <Fragment>
      {obj.approved_at != null ? (
        <span title={gt('Approved %s', moment(obj.approved_at).format('lll'))}>
          {approved_by != null
            ? gt('Approved %(timeago)s by %(user)s', {
                timeago: moment(obj.approved_at).fromNow(),
                user: getUserLink(approved_by),
              })
            : gt('Approved %s', moment(obj.approved_at).fromNow())}
        </span>
      ) : (
        <span title={gt('Never Approved')}>{gt('Never Approved')}</span>
      )}
    </Fragment>
  );
};
export const getReviewedText = (obj: SortedType): JSX.Element => (
  <Fragment>
    {obj.last_review_at !== null ? (
      <span title={gt('Reviewed %s', moment(obj.last_review_at).format('lll'))}>
        {gt('Reviewed %s', moment(obj.last_review_at).fromNow())}
      </span>
    ) : (
      <span title={gt('Never Reviewed')}>{gt('Never Reviewed')}</span>
    )}
  </Fragment>
);
export const getNextReviewText = (obj: SortedType): JSX.Element => (
  <Fragment>
    {obj.next_review_at !== null ? (
      <span
        title={gt('Review due %s', moment(obj.next_review_at).format('lll'))}
      >
        {gt('Review due %s', moment(obj.next_review_at).fromNow())}
      </span>
    ) : (
      <span title={gt('No review period')}>{gt('No review period')}</span>
    )}
  </Fragment>
);
export const getModifiedText = (obj: SortedType) => {
  const {author} = obj;
  return (
    <span title={gt('Edited %s', moment(obj.edited_at).format('lll'))}>
      {author != null
        ? gt('Edited %(timeago)s by %(user)s', {
            timeago: moment(obj.edited_at).fromNow(),
            user: getUserLink(author),
          })
        : gt('Edited %s', moment(obj.edited_at).fromNow())}
    </span>
  );
};

const textFetcher = (
  sortBy: SortOptionType
): ((obj: SortedType) => JSX.Element) => {
  switch (sortBy) {
    case SORT_BY_TYPES.CREATION_DATE:
      return getCreatedText;

    case SORT_BY_TYPES.APPROVED_DATE_DESC:
    case SORT_BY_TYPES.APPROVED_DATE_ASC:
      return getApprovedText;

    case SORT_BY_TYPES.REVIEWED_DATE_DESC:
    case SORT_BY_TYPES.REVIEWED_DATE_ASC:
      return getReviewedText;

    case SORT_BY_TYPES.REVIEWED_DATE_NEXT_ASC:
      return getNextReviewText;

    default:
      return getModifiedText;
  }
};

type Props = {
  object: SortedType;
  sortBy?: SortOptionType;
  backendDefaultSort: SortOptionType;
};
export const ItemLatestActivity = ({
  sortBy,
  object,
  backendDefaultSort,
}: Props) => {
  const getterFunc = textFetcher(sortBy != null ? sortBy : backendDefaultSort);
  return (
    <div className="d-inline text-muted font-italic">
      <small>{getterFunc(object)}</small>
    </div>
  );
};
ItemLatestActivity.defaultProps = {
  backendDefaultSort: SORT_BY_TYPES.MODIFICATION_DATE,
};
